import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { showSuccessToast } from '@/hooks/use-mutation-with-toast';

const money = (value: number, currency: string) =>
  `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(value)} ${currency}`;

export function AccountBankMatching({
  bankMovementId,
  readOnlyPreview,
  onSaved,
  onPendingChange,
}: {
  bankMovementId: string;
  readOnlyPreview: boolean;
  onSaved: (journalEntryId: string, targetName: string, note: string) => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const { t } = useTranslation('trading');
  const utils = trpc.useUtils();
  const canView = usePermission('ACCOUNTING', 'VIEW', 'TRADING');
  const canCreate = usePermission('ACCOUNTING', 'CREATE', 'TRADING');
  const canPost = usePermission('ACCOUNTING', 'EDIT', 'TRADING');
  const [accountId, setAccountId] = useState('');
  const [bankAccountOverride, setBankAccountOverride] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const accounts = trpc.tradingBankMatching.accountOptions.useQuery(
    { bankMovementId },
    { enabled: canView }
  );
  const bankAccountId = bankAccountOverride ?? accounts.data?.suggestedBankAccountId ?? '';
  const selection = { bankMovementId, bankAccountId, accountId };
  const preview = trpc.tradingBankMatching.previewAccount.useQuery(selection, {
    enabled: canView && !!bankAccountId && !!accountId && bankAccountId !== accountId,
  });
  const save = trpc.tradingBankMatching.saveAccount.useMutation({
    onSuccess: async (result) => {
      await Promise.all([
        utils.tradingBankMatching.invalidate(),
        utils.tradingJournalEntry.invalidate(),
        utils.tradingAccount.invalidate(),
      ]);
      showSuccessToast(t('bankMatching.journalSaved', 'Eşleştirme ve yevmiye kaydı oluşturuldu.'));
      if (result.journalEntryId && preview.data)
        onSaved(result.journalEntryId, preview.data.targetName, note.trim());
    },
    onError: () => {
      void preview.refetch();
    },
  });
  const pending = save.isPending;
  useEffect(() => {
    onPendingChange(pending);
    return () => onPendingChange(false);
  }, [pending, onPendingChange]);
  const valid =
    canView &&
    canCreate &&
    canPost &&
    !readOnlyPreview &&
    !pending &&
    !!accountId &&
    !!bankAccountId &&
    accountId !== bankAccountId &&
    !!preview.data &&
    !preview.isFetching &&
    !preview.error &&
    !accounts.isFetching &&
    !accounts.error;
  const options = (rows: { id: string; code: string; name: string; currency: string }[]) =>
    rows.map((a) => ({
      value: a.id,
      label: `${a.code} · ${a.name}`,
      description: a.currency,
      keywords: [a.code, a.name, a.currency],
    }));
  const changeAccount = (value: string) => {
    setAccountId(value);
    save.reset();
  };
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-xs">
        {t(
          'bankMatching.accountHelp',
          'Hesap numarası, muhasebe hesap kodudur. Hareketin tamamı için yevmiye kaydı oluşturulur; cari bakiyesi değişmez.'
        )}
      </p>
      {!canView ? (
        <p role="alert" className="text-sm">
          {t(
            'bankMatching.accountViewPermission',
            'Hesapları görmek için muhasebe görüntüleme yetkisi gerekir.'
          )}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            <Label>{t('bankMatching.bankGlAccount', 'Banka muhasebe hesabı (102)')}</Label>
            <Combobox
              ariaLabel={t('bankMatching.bankGlAccount', 'Banka muhasebe hesabı (102)')}
              value={bankAccountId}
              options={options(accounts.data?.bankAccounts ?? [])}
              onValueChange={(value) => {
                setBankAccountOverride(value);
                save.reset();
              }}
              placeholder={t('bankMatching.selectAccount', 'Muhasebe hesabı seçin')}
              searchPlaceholder={t('bankMatching.searchAccount', 'Hesap numarası veya adı ara')}
              emptyText={t('bankMatching.noAccount', 'Uygun aktif alt hesap bulunamadı.')}
              disabled={pending || accounts.isFetching}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('bankMatching.counterAccount', 'Karşı muhasebe hesabı')}</Label>
            <Combobox
              ariaLabel={t('bankMatching.counterAccount', 'Karşı muhasebe hesabı')}
              value={accountId}
              options={options(
                (accounts.data?.accounts ?? []).filter((a) => a.id !== bankAccountId)
              )}
              onValueChange={changeAccount}
              placeholder={t('bankMatching.selectAccount', 'Muhasebe hesabı seçin')}
              searchPlaceholder={t('bankMatching.searchAccount', 'Hesap numarası veya adı ara')}
              emptyText={t('bankMatching.noAccount', 'Uygun aktif alt hesap bulunamadı.')}
              disabled={pending || accounts.isFetching}
            />
          </div>
          {(accounts.isFetching || preview.isFetching) && (
            <p className="text-muted-foreground flex items-center gap-2 text-xs">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('bankMatching.journalLoading', 'Yevmiye önizlemesi hazırlanıyor…')}
            </p>
          )}
          {preview.data && !preview.error && accountId !== bankAccountId && (
            <div className="space-y-2 rounded-md border p-3 text-xs">
              <p className="font-medium">
                {t('bankMatching.journalPreview', 'Yevmiye önizlemesi')}
              </p>
              <p>{money(preview.data.amount, preview.data.currency)}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2">{t('bankMatching.glAccount', 'Hesap')}</th>
                      <th className="py-2 text-right">{t('bankMatching.glDebit', 'Borç (TL)')}</th>
                      <th className="py-2 text-right">
                        {t('bankMatching.glCredit', 'Alacak (TL)')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.lines.map((line) => (
                      <tr key={line.accountId} className="border-b last:border-0">
                        <td className="py-2 pr-2">
                          {line.code} · {line.name}
                        </td>
                        <td className="whitespace-nowrap py-2 text-right">
                          {money(line.debit, 'TRY')}
                        </td>
                        <td className="whitespace-nowrap py-2 pl-2 text-right">
                          {money(line.credit, 'TRY')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground">
                {t(
                  'bankMatching.accountPostingNote',
                  'Kaydettiğinizde yevmiye işlenir ve muhasebe hesap bakiyeleri güncellenir.'
                )}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="bank-account-note">{t('bankMatching.note', 'Açıklama')}</Label>
            <Textarea
              id="bank-account-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              maxLength={1000}
              disabled={pending}
            />
          </div>
          {(accounts.error || preview.error || save.error) && (
            <p role="alert" className="text-destructive text-sm">
              {save.error?.message || preview.error?.message || accounts.error?.message}
            </p>
          )}
          {(!canCreate || !canPost) && (
            <p className="text-muted-foreground text-xs">
              {t(
                'bankMatching.accountSavePermission',
                'Kaydetmek için muhasebe oluşturma ve işleme yetkileri gerekir.'
              )}
            </p>
          )}
        </>
      )}
      {readOnlyPreview && (
        <p className="text-muted-foreground text-xs">
          {t(
            'bankMatching.readOnlyPreview',
            'Bu önizleme salt okunurdur. Eşleştirmeler canlı uygulamada kaydedilebilir.'
          )}
        </p>
      )}
      <Button
        className="h-auto min-h-8 w-full whitespace-normal"
        disabled={!valid}
        onClick={() => {
          if (valid && preview.data)
            save.mutate({
              ...selection,
              previewKey: preview.data.previewKey,
              note: note.trim() || undefined,
            });
        }}
      >
        {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('bankMatching.saveAccount', 'Hesapla Eşleştir ve Yevmiye Oluştur')}
      </Button>
    </div>
  );
}
